import { FormattedDateTime } from 'components/shared/FormattedTimes';
import moment from 'moment';
import Popper from 'popper.js';
import * as React from 'react';
import { Popover, PopoverBody } from 'reactstrap';
import { slugify } from 'styles/global';
import { EntityDefinition } from 'types';
import BaseDto from 'types/__models__/BaseDto';
import * as Constants from '../../../../../Roc.SharedClientApp/util/Constants';
import CheckCircleSVG from '../svg/CheckCircleSVG';
import DashCircleSVG from '../svg/DashCircleSVG';
import GarbageCanCircleSVG from '../svg/GarbageCanCircleSVG';
import InfoSVG from '../svg/InfoSVG';
import { AutoSuggestItem } from '../auto-suggest/Types';

export interface ListingControlsProps {
	record: Partial<BaseDto>;
	entityDefinition?: EntityDefinition;
	popoverPlacement?: Popper.Placement;
	renderIcons: (record: Partial<BaseDto>) => React.ReactNode;
	uniqueKey?: string;
}

export interface ListingControlsState {
	popoverOpen: boolean;
}

interface DefaultPopoverStatusOnListingProps {
	statusLabel: string;
	statusField: string;
	versionLabel: string;
	record: {
		versionNo?: number;
		createDate?: Date;
		createUser?: string;
		modifyDate?: Date;
		modifyUser?: string;
		externalSystem?: AutoSuggestItem;
	};
}

/**
 * Default popover status info to be displayed within the PopoverBody of the ListingStatusDisplay icon. Can be overriden by setting
 * statusInfoOverrideOnListing in the entityDefinition.
 */
function DefaultPopoverStatusOnListing(props: DefaultPopoverStatusOnListingProps) {
	const { record, statusField, versionLabel, statusLabel } = props;
	return (
		<>
			{record[statusField] ? (
				<>
					<strong>{statusLabel} </strong>
					<em>{record[statusField]}</em>
					<br />
				</>
			) : null}
			{record.versionNo ? (
				<>
					<strong>{versionLabel}: </strong>
					<em>{record.versionNo}</em>
					<br />
				</>
			) : null}
			<>
				<strong>Created By: </strong>
				<em>{record.createUser ? record.createUser : Constants.systemUserName}</em>
				<br />
			</>
			{record.createDate ? (
				<>
					<strong>Created On: </strong>
					<em>
						<FormattedDateTime time={record.createDate} />
					</em>
					<br />
				</>
			) : null}
			{record.modifyDate && !moment(record.createDate).isSame(record.modifyDate) ? (
				<>
					<strong>Modified By: </strong>
					<em>{record.modifyUser ? record.modifyUser : Constants.systemUserName}</em>
					<br />
					<strong>Modified On: </strong>
					<em>
						<FormattedDateTime time={record.modifyDate} />
					</em>
					<br />
				</>
			) : null}
			{record.externalSystem?.label ? (
				<>
					<strong>External System: </strong>
					<em>{record.externalSystem?.label}</em>
					<br />
				</>
			) : null}
		</>
	);
}

const listingItemStatusIconClass = 'listing-item-status-icon';

/**
 * Component that renders the actions for any particular field.
 * These include edit, delete, info, and future options for each individual record.
 *
 * @class ListingControls
 * @extends {React.Component<ListingControlsProps<any>>}
 */
class ListingStatusDisplay extends React.Component<ListingControlsProps, ListingControlsState> {
	public static defaultProps = {
		popoverPlacement: 'right',
		renderIcons: (record) =>
			record.status === 'Published' ? (
				<CheckCircleSVG
					class={listingItemStatusIconClass}
					testId={`listing-status-circle-check-icon-${record.id}`}
				/>
			) : record.status === 'Deleted' ? (
				<GarbageCanCircleSVG
					class={listingItemStatusIconClass}
					testId={`listing-status-garbage-can-circle-${record.id}`}
				/>
			) : record.status === 'Inactive' ? (
				<DashCircleSVG class={listingItemStatusIconClass} testId={`listing-status-dash-icon-${record.id}`} />
			) : (
				<InfoSVG class={listingItemStatusIconClass} testId={`listing-status-info-icon-${record.id}`} />
			),
	};

	constructor(props) {
		super(props);

		this.state = {
			popoverOpen: false,
		};
	}

	public shouldComponentUpdate(nextProps: ListingControlsProps, nextState: ListingControlsState) {
		return (
			nextProps.record.id !== this.props.record.id ||
			nextState.popoverOpen !== this.state.popoverOpen ||
			nextProps.record.status !== this.props.record.status
		);
	}

	public handleButtonClick = (e) => {
		// prevent form submit
		e.preventDefault();

		this.toggle();
	};

	public toggle = () => {
		this.setState({
			popoverOpen: !this.state.popoverOpen,
		});
	};

	public render() {
		const { record, renderIcons, entityDefinition, uniqueKey } = this.props;

		const statusLabel = entityDefinition?.statusLabel;
		const statusField = entityDefinition?.statusField;

		const versionLabel = entityDefinition?.versionLabel ? entityDefinition.versionLabel : 'Version';

		const popoverUniqueKey = uniqueKey ? '-' + uniqueKey : '';

		const popoverTarget = 'Popover-' + slugify(record.id + popoverUniqueKey);

		return (
			<>
				<button
					data-testid={popoverTarget}
					className="listing-status-btn"
					id={popoverTarget}
					onClick={this.handleButtonClick}
					aria-expanded={this.state.popoverOpen}
				>
					{renderIcons(record)}
					<span className="visually-hidden">
						{this.state.popoverOpen ? 'Close' : 'Open'} Status Information
					</span>
				</button>
				<Popover
					target={popoverTarget}
					placement={this.props.popoverPlacement}
					isOpen={this.state.popoverOpen}
					toggle={this.toggle}
					className="popover-wide"
				>
					<PopoverBody data-testid={`popover-body-${record.id}`}>
						{entityDefinition === undefined ? null : !entityDefinition.statusInfoOverrideOnListing ? (
							<DefaultPopoverStatusOnListing
								record={record}
								statusField={statusField || ''}
								statusLabel={statusLabel || ''}
								versionLabel={versionLabel}
							/>
						) : (
							entityDefinition.statusInfoOverrideOnListing(record)
						)}
					</PopoverBody>
				</Popover>
			</>
		);
	}
}

export default ListingStatusDisplay;
